import type { ReactNode } from "react";
import { Dialog } from "@jkast90/kast-common/ui";
import DialogBanner from "./DialogBanner";
import styles from "./BaseDialog.module.css";

/**
 * The app's modal: kast-common's Dialog (the native <dialog>: Esc, the
 * backdrop and × ask `onClose`; the browser gives it the focus trap, the
 * inert page behind it and the scroll lock) with the banner at the top of
 * its body. `title` is its heading and its accessible name; `fill` makes it
 * a tall one whose form (and editor) fills the height; `footer` is its button
 * row (`DialogActions`), kept in view while the body scrolls.
 */
export default function BaseDialog({
  open,
  onClose,
  children,
  footer,
  className = "",
  title,
  fill = false,
  dismissable = true,
  "aria-label": ariaLabel,
  "data-testid": testId = undefined,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  title?: ReactNode;
  fill?: boolean;
  /** False blocks Esc, the backdrop and × (while a save runs). */
  dismissable?: boolean;
  /** The name when there is no `title`. */
  "aria-label"?: string;
  "data-testid"?: string;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      aria-label={title ? undefined : (ariaLabel ?? "Dialog")}
      dismissable={dismissable}
      size="lg"
      footer={footer}
      className={[styles.dialog, fill ? styles.fill : "", className]
        .filter(Boolean)
        .join(" ")}
    >
      <DialogBanner />
      {testId ? <div data-testid={testId}>{children}</div> : children}
    </Dialog>
  );
}
